declare module "@mui/material/styles" {
  interface PaletteColor {
    500?: string
  }

  interface SimplePaletteColorOptions {
    500?: string
  }

  interface TypographyVariants {
    bodyLarge: React.CSSProperties
    bodyMedium: React.CSSProperties
    bodySmall: React.CSSProperties
    bodyXsmall: React.CSSProperties
    bodyXlarge: React.CSSProperties
    bodyCompactLarge: React.CSSProperties
    bodyCompactMedium: React.CSSProperties
    bodyCompactSmall: React.CSSProperties
    labelLarge: React.CSSProperties
    labelMedium: React.CSSProperties
    labelSmall: React.CSSProperties
    labelSmallBold: React.CSSProperties
    paragraphLarge: React.CSSProperties
    paragraphMedium: React.CSSProperties
    paragraphSmall: React.CSSProperties
    inputValue: React.CSSProperties
    radioLabel: React.CSSProperties
    inputLabel: React.CSSProperties
    helptext: React.CSSProperties
    overlineMedium: React.CSSProperties
    overlineSmall: React.CSSProperties
    caption: React.CSSProperties
    headerLarge: React.CSSProperties
    headerMedium: React.CSSProperties
    headerSmall: React.CSSProperties
    contentLarge: React.CSSProperties
    contentMedium: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bodyLarge?: React.CSSProperties
    bodyMedium?: React.CSSProperties
    bodySmall?: React.CSSProperties
    bodyXsmall?: React.CSSProperties
    bodyXlarge?: React.CSSProperties
    bodyCompactLarge?: React.CSSProperties
    bodyCompactMedium?: React.CSSProperties
    bodyCompactSmall?: React.CSSProperties
    labelLarge?: React.CSSProperties
    labelMedium?: React.CSSProperties
    labelSmall?: React.CSSProperties
    labelSmallBold?: React.CSSProperties
    paragraphLarge?: React.CSSProperties
    paragraphMedium?: React.CSSProperties
    paragraphSmall?: React.CSSProperties
    inputValue: React.CSSProperties
    radioLabel: React.CSSProperties
    inputLabel: React.CSSProperties
    helptext: React.CSSProperties
    overlineMedium: React.CSSProperties
    overlineSmall: React.CSSProperties
    caption: React.CSSProperties
    headerLarge: React.CSSProperties
    headerMedium: React.CSSProperties
    headerSmall: React.CSSProperties
    contentLarge: React.CSSProperties
    contentMedium: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    bodyLarge: true
    bodyMedium: true
    bodySmall: true
    bodyXsmall: true
    bodyXlarge: true
    bodyCompactLarge: true
    bodyCompactMedium: true
    bodyCompactSmall: true
    labelLarge: true
    labelMedium: true
    labelSmall: true
    labelSmallBold: true
    paragraphLarge: true
    paragraphMedium: true
    paragraphSmall: true
    inputValue: true
    radioLabel: true
    inputLabel: true
    helptext: true
    subtitle1: false
    subtitle2: false
    body1: false
    body2: false
    caption: true
    overlineMedium: true
    overlineSmall: true
    headerLarge: true
    headerMedium: true
    headerSmall: true
    contentLarge: true
    contentMedium: true
  }
}

const typographyStyle = {
  fontFamily: ['area-normal', 'Fira Sans', 'sans-serif'].join(','),
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  titleLarge: {
    fontSize: "3.75rem", // 60px
    fontWeight: "600",
    lineHeight: "72px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  titleMedium: {
    fontSize: "2.5rem", // 40px
    fontWeight: "600",
    lineHeight: "64px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  h1: {
    fontSize: "1.5rem", // 24px
    fontWeight: "600",
    lineHeight: "38px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  h2: {
    fontSize: "1.25rem", // 20px
    fontWeight: "700",
    lineHeight: "24px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  h3: {
    fontSize: "1.25rem", // 20px
    fontWeight: "600",
    lineHeight: "24px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  h4: {
    fontSize: "1.125rem", // 18px
    fontWeight: "600",
    lineHeight: "24px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  h5: {
    fontSize: "1rem", // 16px
    fontWeight: "600",
    lineHeight: "24px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  h6: {
    fontSize: "0.938rem", // 15px
    fontWeight: "700",
    lineHeight: "24px",
    letterSpacing: "0px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyXlarge: {
    fontSize: "1.125rem", // 18px
    fontWeight: "400",
    lineHeight: "28px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyLarge: {
    fontSize: "1rem", // 16px
    fontWeight: "400",
    lineHeight: "28px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyMedium: {
    fontSize: "0.875rem", // 14px
    fontWeight: "400",
    lineHeight: "24px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodySmall: {
    fontSize: "0.75rem", // 12px
    fontWeight: "400",
    lineHeight: "20px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyXsmall: {
    fontSize: "0.625rem", // 10px
    fontWeight: "400",
    lineHeight: "150%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyCompactLarge: {
    fontSize: "1.125rem", // 18px
    fontWeight: "400",
    lineHeight: "130%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyCompactMedium: {
    fontSize: "1rem", // 16px
    fontWeight: "400",
    lineHeight: "130%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  bodyCompactSmall: {
    fontSize: "0.875rem", // 14px
    fontWeight: "400",
    lineHeight: "130%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  labelLarge: {
    fontSize: "1rem", // 16px
    fontWeight: "300",
    lineHeight: "100%",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  labelMedium: {
    fontSize: "0.875rem", // 14px
    fontWeight: "400",
    lineHeight: "100%",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  labelSmall: {
    fontSize: "0.75rem", // 12px
    fontWeight: "400",
    lineHeight: "150%",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  labelSmallBold: {
    fontSize: "0.75rem", // 12px
    fontWeight: "600",
    lineHeight: "150%",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  paragraphLarge: {
    fontSize: "1.125rem", // 18px
    fontWeight: "400",
    lineHeight: "150%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  paragraphMedium: {
    fontSize: "1rem", // 16px
    fontWeight: "400",
    lineHeight: "150%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  paragraphSmall: {
    fontSize: "0.875rem", // 14px
    fontWeight: "400",
    lineHeight: "140%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  inputValue: {
    fontSize: "1rem", // 16px
    fontWeight: "400",
    lineHeight: "125%",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  radioLabel: {
    fontSize: "1rem", // 16px
    fontWeight: "500",
    lineHeight: "100%",
    display: "block",
    letterSpacing: "-0.24px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  inputLabel: {
    fontSize: "1rem", // 16px
    fontWeight: "500",
    lineHeight: "187.5%",
    display: "block",
    letterSpacing: "-0.24px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  helptext: {
    fontSize: "0.75rem", // 12px
    fontWeight: "500",
    lineHeight: "110%",
    display: "block",
    letterSpacing: "-0.18px",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  overlineMedium: {
    fontSize: "1rem", // 16px
    fontWeight: "400",
    lineHeight: "22px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  overlineSmall: {
    fontSize: "0.75rem", // 12px
    fontWeight: "400",
    lineHeight: "22px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },
  caption: {
    fontSize: "0.75rem", // 12px
    fontWeight: "400",
    lineHeight: "16px",
    letterSpacing: "0px",
    display: "block",
    fontFamily: "area-normal, Fira Sans, sans-serif"
  },

  // data table
  headerLarge: {
    fontSize: "0.875rem", // 14px
    fontWeight: "600",
    lineHeight: "16px",
    letterSpacing: "0px",
    display: "block"
  },
  headerMedium: {
    fontSize: "0.75rem", // 12px
    fontWeight: "600",
    lineHeight: "16px",
    letterSpacing: "0px",
    display: "block"
  },
  contentLarge: {
    fontFamily: ['Fira Sans', 'sans-serif'].join(','),
    fontSize: "0.875rem", // 14px
    fontWeight: "400",
    lineHeight: "16px",
    letterSpacing: "0px",
    display: "block"
  },
  contentMedium: {
    fontFamily: ['Fira Sans', 'sans-serif'].join(','),
    fontSize: "0.75rem", // 12px
    fontWeight: "400",
    lineHeight: "16px",
    letterSpacing: "0px",
    display: "block"
  }

}
export default typographyStyle
